import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext, useFormContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Smartphone, X } from 'lucide-react'
import { useState } from 'react'
import CollectionStorefrontVisibilityIndicator, {
  getCollectionStorefrontVisibilityMessage,
} from './CollectionStorefrontVisibilityIndicator'
import {
  ANNOUNCEMENT_LABEL_MAX_LENGTH,
  isSafeNavigationDestination,
  NAVIGATION_DESTINATION_MAX_LENGTH,
} from './navigationMenuEditor.utils'

const validateItemLabel = ({ value }) => {
  if (typeof value === 'string' && value.length > ANNOUNCEMENT_LABEL_MAX_LENGTH) {
    return `Labels cannot be over ${ANNOUNCEMENT_LABEL_MAX_LENGTH} characters.`
  }

  return undefined
}

const validateLinkDestination = ({ value }) => {
  if (typeof value !== 'string' || !value.trim()) {
    return 'Enter a destination for this link.'
  }

  if (value.length > NAVIGATION_DESTINATION_MAX_LENGTH) {
    return `Link URLs cannot be over ${NAVIGATION_DESTINATION_MAX_LENGTH} characters.`
  }

  if (!isSafeNavigationDestination(value)) {
    return 'Use an internal path beginning with / or a full http:// or https:// URL.'
  }

  return undefined
}

const MobileVisibilityIndicator = ({ showOnMobile }) => (
  <span
    className={cn(
      'inline-flex items-center',
      showOnMobile ? 'text-interaction' : 'text-muted-foreground'
    )}
    title={showOnMobile ? 'Visible on mobile' : 'Hidden on mobile'}
    aria-label={showOnMobile ? 'Visible on mobile' : 'Hidden on mobile'}
  >
    <span className="relative inline-flex size-3 items-center justify-center">
      <Smartphone className="size-3" />
      {!showOnMobile && <span className="absolute h-px w-3 rotate-[-32deg] bg-current" />}
    </span>
  </span>
)

const NavItem = ({
  id,
  isOverlay,
  canDelete = false,
  canEdit = false,
  collectionVisibilityIssue = null,
}) => {
  const field = useFieldContext()
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()
  const [isOpen, setIsOpen] = useState(false)

  const onOpenChange = (open) => {
    setIsOpen(canEdit && open)
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isReadOnly,
  })

  const index = field.state.value.findIndex((item) => item?.id === id)
  if (index === -1) return null
  const name = (str) => `${field.name}.${index}.${str}`
  const navItem = field.state.value[index]
  const label = navItem?.type === 'collection' ? navItem?.collection?.name : navItem?.label
  const hasMobileVisibilitySetting = typeof navItem?.props?.showOnMobile === 'boolean'
  const showOnMobile = navItem?.props?.showOnMobile === true

  const style = {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 9999 : 1,
    transition,
  }

  const handleDeleteClick = () => {
    if (isDragging || isReadOnly || !canDelete) return
    void form.removeFieldValue(field.name, index)
  }

  if (isOverlay) {
    return (
      <Badge
        size="md"
        className={'py-2 touch-none cursor-grabbing relative! overflow-visible z-10'}
      >
        {canDelete && (
          <X className="absolute -top-3! -right-2! z-20 size-5! rounded-full bg-destructive p-0.5 text-destructive-foreground" />
        )}
        <span>{label || <span className="invisible">Blank</span>}</span>
        {(hasMobileVisibilitySetting || collectionVisibilityIssue) && (
          <span className="ml-1 inline-flex items-center gap-1">
            {hasMobileVisibilitySetting && (
              <MobileVisibilityIndicator showOnMobile={showOnMobile} />
            )}
            {collectionVisibilityIssue && (
              <CollectionStorefrontVisibilityIndicator
                visibilityIssue={collectionVisibilityIssue}
                withTooltip={false}
              />
            )}
          </span>
        )}
      </Badge>
    )
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      id={id}
      data-nav-item-label={label || ''}
      className={cn(
        'relative inline-flex items-center list-none cursor-pointer',
        isDragging && 'invisible'
      )}
    >
      {canDelete && (
        <X
          onClick={handleDeleteClick}
          className={cn(
            'absolute -top-3 -right-2 z-10 size-5 rounded-full bg-destructive p-0.5 text-destructive-foreground'
          )}
        />
      )}
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Badge
            size="md"
            {...listeners}
            data-nav-drag-handle=""
            data-nav-item-label={label || ''}
            className="py-2 touch-none select-none"
          >
            <span>{label || <span className="invisible">Blank</span>}</span>
            {(hasMobileVisibilitySetting || collectionVisibilityIssue) && (
              <span className="ml-1 inline-flex items-center gap-1">
                {hasMobileVisibilitySetting && (
                  <MobileVisibilityIndicator showOnMobile={showOnMobile} />
                )}
                {collectionVisibilityIssue && (
                  <CollectionStorefrontVisibilityIndicator
                    visibilityIssue={collectionVisibilityIssue}
                  />
                )}
              </span>
            )}
          </Badge>
        </PopoverTrigger>
        <PopoverContent size="small" className="mx-2">
          <div className="flex flex-col gap-4">
            <form.AppField
              name={name('label')}
              validators={{ onChange: validateItemLabel, onBlur: validateItemLabel }}
            >
              {(field) => <field.TextInput label="Label" />}
            </form.AppField>
            {navItem?.type === 'link' && (
              <form.AppField
                name={name('href')}
                validators={{ onChange: validateLinkDestination, onBlur: validateLinkDestination }}
              >
                {(field) => (
                  <field.TextInput
                    label="Link URL"
                    placeholder="/your-url"
                    onBlur={(event) => field.handleChange(event.target.value.trim())}
                  />
                )}
              </form.AppField>
            )}
            {navItem?.type === 'collection' && collectionVisibilityIssue && (
              <p className="text-xs text-muted-foreground">
                {getCollectionStorefrontVisibilityMessage(collectionVisibilityIssue)}
              </p>
            )}
            <form.AppField name={name('props.showOnMobile')}>
              {(field) => <field.SwitchInput label="Show on Mobile" />}
            </form.AppField>
          </div>
        </PopoverContent>
      </Popover>
    </li>
  )
}

export default NavItem
